import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, Loader2, Plus, X } from 'lucide-react';
import { cn } from '@/shared/utils/helpers';
import { COMMON_UNITS } from '../mockData';
import {
  onboardingDualPanelGridClass,
  onboardingStepShellClass
} from '../Utils/onboardingLayout';
import { parsePackageSizeInput } from '../Utils/UnitDefinition.utils';
import { getStockDisplayUnit } from '../Utils/Inventory.utils';
import type {
  CreateStorageLocationPayload,
  CreateInventoryItemPayload,
  InventoryItemCategory,
  InventoryItemDraft,
  LocationSupplier,
  StorageLocationNode
} from '../Types';
import { InventoryItemsPanel } from './InventoryItemsPanel';
import type { UpdateRestaurantInventoryItemPayload } from '../Api/Inventory.api';

/** Purchase/main units aligned with InventoryUnitSelect / Nest inventory UI */
const UNITS = COMMON_UNITS.map(u => u.value);

/** Units that typically contain a pack-out quantity */
const CONTAINER_UNITS = new Set([
  'cs',
  'box',
  'btl',
  'pk',
  'packs',
  'doz',
  'L'
]);

const UNIT_LABEL: Record<string, string> = Object.fromEntries(
  COMMON_UNITS.map(u => [u.value, u.label])
);

const UNCOUNTABLE = [
  'bleach',
  'oil',
  'water',
  'milk',
  'cream',
  'sauce',
  'vinegar',
  'juice',
  'syrup',
  'soap',
  'detergent',
  'cleaner',
  'sanitizer',
  'alcohol',
  'grease',
  'butter'
];

function getPackageQuestion(
  itemName: string,
  unit: string
): { question: string; placeholder: string } {
  const unitLabel = (UNIT_LABEL[unit] || unit).toLowerCase();
  const name = itemName.trim().toLowerCase();
  if (!name) {
    return {
      question: `What's in 1 ${unitLabel}?`,
      placeholder: `e.g. 12, 12 packs, 12x500ml`
    };
  }

  const isUncountable = UNCOUNTABLE.some(w => name.includes(w));
  if (isUncountable) {
    return {
      question: `What's in 1 ${unitLabel}?`,
      placeholder: `e.g. 4, 4 bottles, 4x32oz`
    };
  }

  const words = name.split(/\s+/).filter(w => w.length > 1);
  const lastWord = words[words.length - 1] || name;

  return {
    question: `How many ${lastWord} in a ${unitLabel}?`,
    placeholder: `e.g. 12, 12 packs, 12-pack`
  };
}

export interface ListedInventoryItem {
  localId: string;
  restaurantInventoryItemId?: string | null;
  stockId?: string | null;
  name: string;
  /** Purchase / order unit, e.g. cs */
  unit: string;
  /** Display unit for quantityOnHand/parLevel — base when conversion exists */
  stockUnit: string;
  unitMultiplier?: number | null;
  baseUnit?: string | null;
  categoryName?: string | null;
  /** True when category came from AI import suggestion */
  categorySuggested?: boolean;
  /** True when storage placement came from AI / heuristics */
  locationSuggested?: boolean;
  /** Pending room label when suggested location does not exist yet */
  locationSuggestedPath?: string | null;
  packageSizeLabel?: string | null;
  quantityOnHand?: number | null;
  parLevel?: number | null;
  /** MOQ in purchase unit */
  minOrderQty?: number | null;
  supplierName?: string | null;
  supplierUserId?: string | null;
  locationId?: string | null;
}

const normalizeItemName = (value: string) => value.trim().toLowerCase();

/** Synthetic select value for an AI-suggested category that is not in the list yet. */
const AI_PENDING_CATEGORY_PREFIX = '__ai_pending__:';

const toPendingCategoryId = (name: string) =>
  `${AI_PENDING_CATEGORY_PREFIX}${name.trim().toLowerCase()}`;

const parsePendingCategoryName = (id: string): string | null => {
  if (!id.startsWith(AI_PENDING_CATEGORY_PREFIX)) return null;
  const name = id.slice(AI_PENDING_CATEGORY_PREFIX.length).trim();
  return name || null;
};

const findCategoryByName = (
  categories: InventoryItemCategory[],
  name: string | null | undefined
): InventoryItemCategory | undefined => {
  const normalized = (name ?? '').trim().toLowerCase();
  if (!normalized) return undefined;
  return categories.find(c => c.name.trim().toLowerCase() === normalized);
};

const findDuplicateListedItem = (
  items: ListedInventoryItem[],
  itemName: string,
  excludeLocalId?: string | null
): ListedInventoryItem | null => {
  const normalized = normalizeItemName(itemName);
  if (!normalized) return null;
  return (
    items.find(
      item =>
        normalizeItemName(item.name) === normalized &&
        item.localId !== excludeLocalId
    ) ?? null
  );
};

interface AddItemsFormProps {
  categories: InventoryItemCategory[];
  suppliers: LocationSupplier[];
  items: ListedInventoryItem[];
  defaultLocationId: string | null;
  isLive: boolean;
  submitting?: boolean;
  deletingId?: string | null;
  updatingId?: string | null;
  storageLocations: StorageLocationNode[];
  storageLocationsLoading?: boolean;
  creatingStorageLocation?: boolean;
  /** When set (e.g. after Add supplier), select this supplier in the dropdown. */
  preferredSupplierId?: string | null;
  /** When set (e.g. after Add category modal), select this category. */
  preferredCategoryId?: string | null;
  onAddItem: (payload: CreateInventoryItemPayload) => Promise<void>;
  onUpdateItem: (
    item: ListedInventoryItem,
    payload: UpdateRestaurantInventoryItemPayload
  ) => Promise<void>;
  onDeleteItem: (item: ListedInventoryItem) => Promise<void>;
  onUpdateQuantity: (
    item: ListedInventoryItem,
    quantity: number
  ) => Promise<void>;
  onAssignLocation: (
    item: ListedInventoryItem,
    locationId: string
  ) => Promise<void>;
  onCreateStorageLocation?: (
    payload: CreateStorageLocationPayload
  ) => Promise<StorageLocationNode>;
  /** Create (or return existing) category by name — used for AI suggestions. */
  onEnsureCategory?: (name: string) => Promise<InventoryItemCategory>;
  onDone?: () => void | Promise<void>;
  /** Label for the primary footer action (default: Continue to storage). */
  doneLabel?: string;
  doneDisabled?: boolean;
  doneLoading?: boolean;
  onBack?: () => void;
  onRequestAddSupplier?: () => void;
  onRequestAddCategory?: () => void;
  /** Banner above the title (import status, warnings) — same width as the panels */
  statusNotice?: ReactNode;
}

export const draftToListedItem = (
  draft: InventoryItemDraft,
  categories: InventoryItemCategory[],
  suppliers: LocationSupplier[]
): ListedInventoryItem => {
  const category =
    categories.find(
      c => c.name.toLowerCase() === draft.note.trim().toLowerCase()
    )?.name ??
    (draft.note.trim() || null);
  const supplier =
    suppliers.find(s => s.supplier_id === draft.supplierUserId)
      ?.business_name ?? null;
  const packageSizeLabel =
    draft.unitMultiplier.trim() && draft.baseUnit.trim()
      ? `${draft.unitMultiplier} ${draft.baseUnit}`
      : draft.unitMultiplier.trim() || null;

  return {
    localId: draft.localId,
    restaurantInventoryItemId: draft.restaurantInventoryItemId,
    stockId: draft.stockId,
    name: draft.name,
    unit: draft.unit,
    stockUnit: getStockDisplayUnit(draft.unit, draft.baseUnit),
    unitMultiplier: draft.unitMultiplier.trim()
      ? Number(draft.unitMultiplier)
      : null,
    baseUnit: draft.baseUnit.trim() || null,
    categoryName: category,
    categorySuggested: Boolean(draft.categorySuggested) && Boolean(category),
    locationSuggested: Boolean(draft.locationSuggested),
    locationSuggestedPath: draft.locationSuggestedPath?.trim() || null,
    packageSizeLabel,
    quantityOnHand: draft.quantityOnHand.trim()
      ? Number(draft.quantityOnHand)
      : null,
    parLevel: draft.minQuantity.trim() ? Number(draft.minQuantity) : null,
    minOrderQty: draft.minOrderQty.trim()
      ? Number(draft.minOrderQty)
      : null,
    supplierName: supplier,
    supplierUserId: draft.supplierUserId.trim() || null,
    locationId: draft.locationId
  };
};

const formatSupplierOption = (supplier: LocationSupplier): string => {
  const name = supplier.business_name?.trim() || 'Unnamed supplier';
  if (supplier.network_type === 'out_of_network') {
    return `${name} (off-network)`;
  }
  return name;
};

export const AddItemsForm = ({
  categories,
  suppliers,
  items,
  defaultLocationId,
  isLive,
  submitting = false,
  deletingId = null,
  updatingId = null,
  storageLocations,
  storageLocationsLoading = false,
  creatingStorageLocation = false,
  preferredSupplierId = null,
  preferredCategoryId = null,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onUpdateQuantity,
  onAssignLocation,
  onCreateStorageLocation,
  onEnsureCategory,
  onDone,
  doneLabel = 'Continue to storage',
  doneDisabled = false,
  doneLoading = false,
  onBack,
  onRequestAddSupplier,
  onRequestAddCategory,
  statusNotice
}: AddItemsFormProps) => {
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [unit, setUnit] = useState<string>('cs');
  const [qty, setQty] = useState('');
  const [parLevel, setParLevel] = useState('');
  const [minOrderQty, setMinOrderQty] = useState('');
  const [packageSize, setPackageSize] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<ListedInventoryItem | null>(
    null
  );
  const [ensuringCategory, setEnsuringCategory] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const resolveCategorySelectValue = (item: ListedInventoryItem): string => {
    const matched = findCategoryByName(categories, item.categoryName);
    if (matched) return matched.id;
    if (item.categoryName?.trim()) {
      return toPendingCategoryId(item.categoryName);
    }
    return '';
  };

  /** AI / import labels that are not restaurant categories yet — show in the select. */
  const pendingCategoryOptions = useMemo(() => {
    const byKey = new Map<string, { value: string; label: string }>();
    for (const item of items) {
      const label = item.categoryName?.trim();
      if (!label) continue;
      if (findCategoryByName(categories, label)) continue;
      const value = toPendingCategoryId(label);
      if (!byKey.has(value)) {
        byKey.set(value, { value, label });
      }
    }
    if (editingItem?.categoryName?.trim()) {
      const label = editingItem.categoryName.trim();
      if (!findCategoryByName(categories, label)) {
        const value = toPendingCategoryId(label);
        if (!byKey.has(value)) {
          byKey.set(value, { value, label });
        }
      }
    }
    return [...byKey.values()];
  }, [items, categories, editingItem?.categoryName]);

  const categorySelectValue = useMemo(() => {
    if (!categoryId) return '';
    const pending = parsePendingCategoryName(categoryId);
    if (pending) {
      const existing = findCategoryByName(categories, pending);
      return existing ? existing.id : toPendingCategoryId(pending);
    }
    return categoryId;
  }, [categoryId, categories]);

  const selectedPendingLabel = useMemo(() => {
    const pendingKey = parsePendingCategoryName(categorySelectValue);
    if (!pendingKey) return null;
    return (
      pendingCategoryOptions.find(o => o.value === categorySelectValue)
        ?.label ??
      editingItem?.categoryName?.trim() ??
      pendingKey
    );
  }, [categorySelectValue, pendingCategoryOptions, editingItem?.categoryName]);

  const showPackageQuestion = CONTAINER_UNITS.has(unit);
  const activeSuppliers = suppliers.filter(s => s.active && !s.is_archived);
  const parsedPackage = showPackageQuestion
    ? parsePackageSizeInput(packageSize, unit)
    : null;
  // Qty/par are in base/stock unit; MOQ is in purchase unit.
  const activeBaseUnit =
    (showPackageQuestion &&
    parsedPackage &&
    'ok' in parsedPackage &&
    parsedPackage.ok
      ? parsedPackage.baseUnit
      : null) ||
    editingItem?.baseUnit ||
    null;
  const qtyParUnit = getStockDisplayUnit(unit, activeBaseUnit);
  const isEditing = Boolean(editingItem);
  const isSaving =
    submitting ||
    ensuringCategory ||
    (editingItem != null && updatingId === editingItem.localId);
  const duplicateItem = useMemo(
    () => findDuplicateListedItem(items, name, editingItem?.localId),
    [editingItem?.localId, items, name]
  );
  const hasDuplicateName = Boolean(duplicateItem);

  const resetFormFields = (keepContext = true) => {
    setName('');
    setQty('');
    setParLevel('');
    setMinOrderQty('');
    setPackageSize('');
    setError(null);
    if (!keepContext) {
      setCategoryId('');
      setUnit('cs');
      setSupplierId('');
    }
  };

  const loadItemIntoForm = (item: ListedInventoryItem) => {
    setEditingItem(item);
    setError(null);
    setName(item.name);
    setUnit(item.unit);
    setQty(
      item.quantityOnHand != null && !Number.isNaN(item.quantityOnHand)
        ? String(item.quantityOnHand)
        : ''
    );
    setParLevel(
      item.parLevel != null && !Number.isNaN(item.parLevel)
        ? String(item.parLevel)
        : ''
    );
    setMinOrderQty(
      item.minOrderQty != null &&
        !Number.isNaN(item.minOrderQty) &&
        item.minOrderQty > 0
        ? String(item.minOrderQty)
        : ''
    );
    setPackageSize(
      item.unitMultiplier != null && item.baseUnit
        ? `${item.unitMultiplier} ${item.baseUnit}`
        : item.packageSizeLabel?.trim() || ''
    );
    setSupplierId(item.supplierUserId?.trim() || '');
    setCategoryId(resolveCategorySelectValue(item));
    requestAnimationFrame(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    });
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setCategoryId('');
    resetFormFields(true);
  };

  // Keep select in sync when opening an item or when categories load.
  useEffect(() => {
    if (!editingItem) return;
    setCategoryId(resolveCategorySelectValue(editingItem));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync on item/category list change
  }, [editingItem?.localId, editingItem?.categoryName, categories]);

  // After import, open the first AI-categorized item so the right form shows the suggestion.
  const didAutoOpenSuggested = useRef(false);
  useEffect(() => {
    if (didAutoOpenSuggested.current || editingItem) return;
    const firstSuggested = items.find(
      i => i.categorySuggested && i.categoryName?.trim()
    );
    if (!firstSuggested) return;
    didAutoOpenSuggested.current = true;
    loadItemIntoForm(firstSuggested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    if (!preferredSupplierId || editingItem) return;
    const exists = suppliers.some(
      s => s.supplier_id === preferredSupplierId && s.active && !s.is_archived
    );
    if (exists) {
      setSupplierId(preferredSupplierId);
    }
  }, [preferredSupplierId, suppliers, editingItem]);

  useEffect(() => {
    if (!preferredCategoryId || editingItem) return;
    if (categories.some(c => c.id === preferredCategoryId)) {
      setCategoryId(preferredCategoryId);
    }
  }, [preferredCategoryId, categories, editingItem]);

  // When categories load/refresh, swap pending AI selection for a real id.
  useEffect(() => {
    const pendingName =
      parsePendingCategoryName(categoryId) ||
      (editingItem?.categorySuggested
        ? editingItem.categoryName?.trim() || null
        : null);
    if (!pendingName) return;
    const matched = findCategoryByName(categories, pendingName);
    if (matched && matched.id !== categoryId) {
      setCategoryId(matched.id);
    }
  }, [categories, categoryId, editingItem]);

  const buildQuantityFields = () => {
    const fields: {
      quantityOnHand?: number;
      minQuantity?: number;
      minOrderQty?: number;
      unitMultiplier?: number;
      baseUnit?: string;
    } = {};

    if (qty.trim() !== '') {
      const qtyNum = Number(qty);
      if (!Number.isNaN(qtyNum) && qtyNum >= 0) {
        fields.quantityOnHand = qtyNum;
      }
    }

    if (parLevel.trim() !== '') {
      const parNum = Number(parLevel);
      if (!Number.isNaN(parNum) && parNum >= 0) {
        fields.minQuantity = parNum;
      }
    }

    if (minOrderQty.trim() !== '') {
      const moqNum = Number(minOrderQty);
      if (!Number.isNaN(moqNum) && moqNum >= 0) {
        fields.minOrderQty = moqNum;
      }
    } else if (editingItem?.minOrderQty != null && editingItem.minOrderQty > 0) {
      // Clear MOQ when user empties the field
      fields.minOrderQty = 0;
    }

    if (showPackageQuestion && packageSize.trim()) {
      const parsed = parsePackageSizeInput(packageSize, unit);
      if (!parsed) {
        // empty after trim — ignore
      } else if (!parsed.ok) {
        return { error: parsed.error } as const;
      } else {
        fields.unitMultiplier = parsed.unitMultiplier;
        fields.baseUnit = parsed.baseUnit;
      }
    }

    return { fields } as const;
  };

  const resolveSelectedCategory = async (): Promise<
    InventoryItemCategory | null | 'error'
  > => {
    const activeCategoryId = categorySelectValue || categoryId;
    const pendingKey = parsePendingCategoryName(activeCategoryId);
    if (pendingKey) {
      const displayName =
        selectedPendingLabel ||
        editingItem?.categoryName?.trim() ||
        pendingKey;
      if (!onEnsureCategory) {
        setError(
          `Category “${displayName}” is not set up yet. Add it first, then save.`
        );
        return 'error';
      }
      setEnsuringCategory(true);
      try {
        const created = await onEnsureCategory(displayName);
        setCategoryId(created.id);
        return created;
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : `Failed to create category “${displayName}”.`
        );
        return 'error';
      } finally {
        setEnsuringCategory(false);
      }
    }

    return categories.find(c => c.id === activeCategoryId) ?? null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (!trimmedName) return;

    if (hasDuplicateName && duplicateItem) {
      nameInputRef.current?.focus();
      return;
    }

    if (isLive && !defaultLocationId && !editingItem?.locationId) {
      setError('No storage area is configured for this location yet.');
      return;
    }

    const quantityResult = buildQuantityFields();
    if ('error' in quantityResult && quantityResult.error) {
      setError(quantityResult.error);
      return;
    }
    if (!('fields' in quantityResult)) {
      setError('Invalid package size.');
      return;
    }
    const { fields } = quantityResult;
    const selectedCategory = await resolveSelectedCategory();
    if (selectedCategory === 'error') return;

    try {
      if (editingItem) {
        const updatePayload: UpdateRestaurantInventoryItemPayload = {
          name: trimmedName,
          unit,
          ...fields,
          locationId:
            editingItem.locationId?.trim() || defaultLocationId || undefined
        };
        if (selectedCategory) {
          updatePayload.categoryId = selectedCategory.id;
        } else if (editingItem.categoryName) {
          updatePayload.clearCategory = true;
        }
        if (supplierId.trim()) {
          updatePayload.supplierUserId = supplierId.trim();
        }
        await onUpdateItem(editingItem, updatePayload);
        setEditingItem(null);
        resetFormFields(true);
      } else {
        const payload: CreateInventoryItemPayload = {
          name: trimmedName,
          unit,
          locationId: defaultLocationId ?? 'preview',
          ...fields
        };
        if (selectedCategory) {
          payload.categoryId = selectedCategory.id;
        }
        if (supplierId.trim()) {
          payload.supplierUserId = supplierId.trim();
        }
        await onAddItem(payload);
        resetFormFields(true);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : isEditing
            ? 'Failed to update item. Try again.'
            : 'Failed to add item. Try again.'
      );
    }
  };

  return (
    <div className={onboardingStepShellClass}>
      {statusNotice ? <div className="w-full">{statusNotice}</div> : null}
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-[#171717]">
          {isEditing ? 'Edit item' : 'Add items'}
        </h2>
        <p className="mt-1 text-sm text-[#6f747c]">
          {isEditing
            ? `Editing ${editingItem?.name}. Save to update, or cancel to add a new item.`
            : 'Everything you order regularly. You can always add more later.'}
        </p>
      </div>

      <div className={onboardingDualPanelGridClass}>
        {/* Left on desktop — inventory list. Form stays first on mobile. */}
        {/* Absolute inner box on lg so the panel fills the shared step height. */}
        <div className="relative order-2 min-w-0 lg:order-1 lg:h-full lg:min-h-0">
          <div className="lg:absolute lg:inset-0">
            <InventoryItemsPanel
              items={items}
              locations={storageLocations}
              defaultLocationId={defaultLocationId}
              deletingId={deletingId}
              updatingId={updatingId}
              editingItemId={editingItem?.localId ?? null}
              locationsLoading={storageLocationsLoading}
              creatingLocation={creatingStorageLocation}
              onDeleteItem={onDeleteItem}
              onUpdateQuantity={onUpdateQuantity}
              onEditItem={loadItemIntoForm}
              onAssignLocation={onAssignLocation}
              onCreateLocation={onCreateStorageLocation}
            />
          </div>
        </div>

        {/* Right on desktop — add / edit form */}
        <form
          onSubmit={e => {
            void handleSubmit(e);
          }}
          className={cn(
            'order-1 space-y-4 rounded-lg border bg-white px-4 pb-5 pt-3 shadow-sm lg:order-2 lg:h-full lg:min-h-0 lg:overflow-y-auto',
            isEditing
              ? 'border-[#087f8c] ring-2 ring-[#087f8c]/10'
              : 'border-[#e7e9ee]'
          )}
        >
          {isEditing && (
            <div className="flex items-center justify-between gap-2 rounded-md border border-[#087f8c]/20 bg-[#087f8c]/5 px-3 py-2">
              <p className="text-xs font-semibold text-[#087f8c]">
                Editing “{editingItem?.name}”
              </p>
              <button
                type="button"
                onClick={cancelEdit}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#6f747c] hover:text-[#171717]"
              >
                <X className="h-3.5 w-3.5" />
                Cancel
              </button>
            </div>
          )}

          <div className="space-y-1.5">
            <label
              htmlFor="inventory-item-name"
              className="text-sm font-semibold text-[#171717]"
            >
              Item name
            </label>
            <input
              id="inventory-item-name"
              ref={nameInputRef}
              placeholder="Ribeye, Olive Oil, Bleach…"
              value={name}
              aria-invalid={hasDuplicateName}
              aria-describedby={
                hasDuplicateName ? 'inventory-item-name-error' : undefined
              }
              onChange={e => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              className={cn(
                'h-9 w-full rounded-md border bg-white px-3 text-sm outline-none transition',
                hasDuplicateName
                  ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                  : 'border-[#e7e9ee] focus:border-[#087f8c] focus:ring-2 focus:ring-[#087f8c]/20'
              )}
              autoFocus
            />
            <AnimatePresence initial={false}>
              {duplicateItem && (
                <motion.div
                  id="inventory-item-name-error"
                  role="alert"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-2"
                >
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-red-700">
                      Already in your inventory
                    </p>
                    <p className="mt-0.5 text-xs text-red-600/90">
                      “{duplicateItem.name}” is already listed
                      {duplicateItem.unit
                        ? ` (${duplicateItem.unit})`
                        : ''}
                      . Edit it instead of adding a duplicate.
                    </p>
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => loadItemIntoForm(duplicateItem)}
                        className="mt-1.5 text-xs font-semibold text-[#087f8c] hover:underline"
                      >
                        Edit existing item
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <label
                  htmlFor="inventory-category-select"
                  className="text-sm font-medium text-[#171717]"
                >
                  Category{' '}
                  <span className="font-normal text-[#6f747c]">— optional</span>
                </label>
                {onRequestAddCategory && (
                  <button
                    type="button"
                    onClick={onRequestAddCategory}
                    className="text-xs font-semibold text-[#087f8c] hover:underline"
                  >
                    Add category
                  </button>
                )}
              </div>
              <select
                id="inventory-category-select"
                key={`category-select-${editingItem?.localId ?? 'new'}-${categorySelectValue}`}
                value={categorySelectValue}
                onChange={e => setCategoryId(e.target.value)}
                className={cn(
                  'h-9 w-full rounded-md border bg-white px-3 text-sm outline-none transition focus:border-[#087f8c] focus:ring-2 focus:ring-[#087f8c]/20',
                  editingItem?.categorySuggested || selectedPendingLabel
                    ? 'border-[#087f8c]/45 bg-[#f3fbfc]'
                    : 'border-[#e7e9ee]'
                )}
              >
                <option value="">Select category</option>
                {pendingCategoryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
                {categories.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {editingItem?.categorySuggested && editingItem.categoryName ? (
                <p className="text-xs text-[#087f8c]">
                  AI suggested
                </p>
              ) : null}
              {categories.length === 0 &&
                pendingCategoryOptions.length === 0 && (
                  <p className="text-xs text-[#6f747c]">
                    No categories yet — use Add category to create one.
                  </p>
                )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#171717]">Unit</label>
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {UNITS.map(u => (
                  <button
                    key={u}
                    type="button"
                    title={UNIT_LABEL[u] || u}
                    onClick={() => {
                      setUnit(u);
                      if (!CONTAINER_UNITS.has(u)) setPackageSize('');
                    }}
                    className={cn(
                      'rounded border px-2.5 py-1 text-sm font-medium transition-colors',
                      unit === u
                        ? 'border-[#087f8c] bg-[#087f8c] text-white'
                        : 'border-[#e7e9ee] bg-white text-[#171717] hover:border-[#087f8c]/50'
                    )}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <AnimatePresence>
            {showPackageQuestion && (
              <motion.div
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: 'auto', marginTop: 4 }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                className="overflow-hidden"
              >
                {(() => {
                  const { question, placeholder } = getPackageQuestion(
                    name,
                    unit
                  );
                  return (
                    <div className="space-y-2 rounded-md border border-[#e7e9ee] bg-[#f6f7f9] px-4 py-3">
                      <label className="text-sm font-medium text-[#171717]">
                        {question}{' '}
                        <span className="font-normal text-[#6f747c]">
                          — optional
                        </span>
                      </label>
                      <input
                        placeholder={placeholder}
                        value={packageSize}
                        onChange={e => setPackageSize(e.target.value)}
                        className="h-9 w-full rounded-md border border-[#e7e9ee] bg-white px-3 text-sm outline-none transition focus:border-[#087f8c] focus:ring-2 focus:ring-[#087f8c]/20"
                      />
                      {parsedPackage?.ok ? (
                        <p className="text-xs text-[#6f747c]">
                          Will save as 1 {unit} = {parsedPackage.unitMultiplier}{' '}
                          {parsedPackage.baseUnit}
                        </p>
                      ) : packageSize.trim() &&
                        parsedPackage &&
                        !parsedPackage.ok ? (
                        <p className="text-xs text-amber-700">
                          {parsedPackage.error}
                        </p>
                      ) : null}
                    </div>
                  );
                })()}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <label
                htmlFor="inventory-supplier-select"
                className="text-sm font-medium text-[#171717]"
              >
                Supplier{' '}
                <span className="font-normal text-[#6f747c]">— optional</span>
              </label>
              {onRequestAddSupplier && (
                <button
                  type="button"
                  onClick={onRequestAddSupplier}
                  className="text-xs font-semibold text-[#087f8c] hover:underline"
                >
                  Add supplier
                </button>
              )}
            </div>
            <select
              id="inventory-supplier-select"
              value={supplierId}
              onChange={e => setSupplierId(e.target.value)}
              className="h-9 w-full rounded-md border border-[#e7e9ee] bg-white px-3 text-sm outline-none transition focus:border-[#087f8c] focus:ring-2 focus:ring-[#087f8c]/20"
            >
              <option value="">Select supplier</option>
              {activeSuppliers.map(s => (
                <option key={s.supplier_id} value={s.supplier_id}>
                  {formatSupplierOption(s)}
                </option>
              ))}
            </select>
            {activeSuppliers.length === 0 && (
              <p className="text-xs text-[#6f747c]">
                No suppliers yet — use Add supplier to create one.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#171717]">
                Qty on hand in {qtyParUnit}{' '}
                <span className="font-normal text-[#6f747c]">— optional</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="e.g. 4"
                value={qty}
                onChange={e => {
                  const next = e.target.value;
                  if (next === '' || /^\d*\.?\d*$/.test(next)) setQty(next);
                }}
                className="h-9 w-full rounded-md border border-[#e7e9ee] bg-white px-3 text-sm text-[#171717] outline-none transition placeholder:italic placeholder:font-normal placeholder:text-[#a8a8a8] focus:border-[#087f8c] focus:ring-2 focus:ring-[#087f8c]/20"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#171717]">
                Par level in {qtyParUnit}{' '}
                <span className="font-normal text-[#6f747c]">— optional</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="e.g. 6"
                value={parLevel}
                onChange={e => {
                  const next = e.target.value;
                  if (next === '' || /^\d*\.?\d*$/.test(next))
                    setParLevel(next);
                }}
                className="h-9 w-full rounded-md border border-[#e7e9ee] bg-white px-3 text-sm text-[#171717] outline-none transition placeholder:italic placeholder:font-normal placeholder:text-[#a8a8a8] focus:border-[#087f8c] focus:ring-2 focus:ring-[#087f8c]/20"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#171717]">
              Min order qty (MOQ) in {unit}{' '}
              <span className="font-normal text-[#6f747c]">— optional</span>
            </label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="e.g. 2"
              value={minOrderQty}
              onChange={e => {
                const next = e.target.value;
                if (next === '' || /^\d*\.?\d*$/.test(next))
                  setMinOrderQty(next);
              }}
              className="h-9 w-full rounded-md border border-[#e7e9ee] bg-white px-3 text-sm text-[#171717] outline-none transition placeholder:italic placeholder:font-normal placeholder:text-[#a8a8a8] focus:border-[#087f8c] focus:ring-2 focus:ring-[#087f8c]/20"
            />
            <p className="text-xs text-[#6f747c]">
              Purchase unit for ordering. Par and on-hand use{' '}
              {qtyParUnit === unit ? 'the same unit' : qtyParUnit}.
            </p>
          </div>

          {error && (
            <p className="rounded-md border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </p>
          )}

          <div className="flex gap-2">
            {isEditing && (
              <button
                type="button"
                onClick={cancelEdit}
                className="inline-flex flex-1 items-center justify-center rounded-md border border-[#e7e9ee] px-3 py-2 text-sm font-semibold text-[#6f747c] transition hover:bg-[#f0f2f5] hover:text-[#171717]"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={!name.trim() || isSaving || hasDuplicateName}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-[#087f8c] px-3 py-2 text-sm font-semibold text-white transition hover:bg-[#066873] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isEditing ? null : (
                <Plus className="h-4 w-4" />
              )}
              {isEditing ? 'Save changes' : 'Add item'}
            </button>
          </div>
        </form>
      </div>

      <div className="flex items-center gap-3 border-t border-[#e7e9ee] pt-4">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="rounded-md px-3 py-2 text-sm font-medium text-[#6f747c] transition hover:bg-[#f0f2f5] hover:text-[#171717]"
          >
            Back
          </button>
        )}
        {onDone && (
          <button
            type="button"
            onClick={() => {
              void onDone();
            }}
            disabled={doneDisabled || doneLoading || submitting}
            className="ml-auto inline-flex items-center gap-2 rounded-md bg-[#087f8c] px-3 py-2 text-sm font-semibold text-white transition hover:bg-[#066873] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {doneLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            {doneLabel}
          </button>
        )}
      </div>
    </div>
  );
};
